import { useEffect, useState, useRef, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Stack,
  Chip,
  Tooltip,
  GlobalStyles,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useLocation, useParams, Navigate, useNavigate } from 'react-router-dom';
import { getCategory } from '../api/categoryApi';
import { getQuestionsByDomain } from '../api/questionApi';
import { getPatientById, getPatientHistory } from '../api/patientApi';
import { getDomainProgress, levelColors } from '../components/CategoryLevels';
import BarChartLevel from '../components/BarChartLevel';
import dayjs from 'dayjs';
import logo from '../assets/logo.png';
import { useReportExport } from '../hooks/useReportExport';
import { getAllPatientReports } from '../api/answersApi';
import { getActiveAssessment, finishAssessment } from '../api/saasApi';
import { useMe } from '../context/MeContext';
import { useSnackbar } from '../components/Snackbar';
import { scoredDomainKeys, isDomainScored } from '../utils/assessmentProgress';

const ReportDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const reportRef = useRef();

  const [domains, setDomains] = useState([]);
  const [therapyDescriptions, setTherapyDescriptions] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [patientDetails, setPatientDetails] = useState(null);
  const [checkedDescriptions, setCheckedDescriptions] = useState({});  
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [timeframe, setTimeframe] = useState("1month");

  // Identity comes from the URL. Router state carries the already-loaded
  // reports as a fast path, but the page must also stand on its own so a
  // refresh or a shared link doesn't land on an empty report.
  const { patientId: routePatientId } = useParams();
  const data = location.state || {};
  const patientId = routePatientId || data.patientId;
  const { patientName, staffName } = data;

  const [fetchedReports, setFetchedReports] = useState(null);
  const reportDataArray = data.reportDataArray || fetchedReports;

  // ---- Per-assessment payment: opening the report completes it -------------
  // For payment-gated clinics, viewing the current progress report IS the end
  // of the paid screening. If the patient has a Paid (open) assessment, the
  // report stays hidden until the assessment is marked Completed; after that,
  // any further scoring needs a new payment. This gate lives here (not on the
  // buttons that link to this page) so every entry point is covered: the
  // domain "saved" dialog, the domains list, patient detail and History.
  // Older reports opened from History are read-only and never gated. The
  // exempt legacy tenant and platform admins are never gated.
  const { billing, isExempt, isPlatformAdmin } = useMe();
  const { showSnackbar } = useSnackbar();
  const gated = !!billing && !isExempt && !isPlatformAdmin;
  const isCurrentReport = !data.reportDataArray || data.isLatest === true;
  const needsGate = gated && isCurrentReport && !!patientId;
  // checking | confirm | completing | open | error
  const [gateState, setGateState] = useState(needsGate ? 'checking' : 'open');
  const [activeAssessment, setActiveAssessment] = useState(null);
  const [gateCheckKey, setGateCheckKey] = useState(0);
  const [gateHistory, setGateHistory] = useState([]);

  useEffect(() => {
    if (!needsGate) { setGateState('open'); return undefined; }
    let cancelled = false;
    setGateState('checking');
    (async () => {
      try {
        const res = await getActiveAssessment(patientId);
        if (cancelled) return;
        const a = res.data?.data;
        if (a && a.status === 'Paid') {
          try {
            const hist = await getPatientHistory(patientId);
            if (!cancelled) setGateHistory(hist.data?.data || []);
          } catch (e) { /* count is informational only */ }
          if (cancelled) return;
          setActiveAssessment(a);
          setGateState('confirm');
        } else {
          setActiveAssessment(null);
          setGateState('open');
        }
      } catch (e) {
        // Fail closed: never reveal an in-progress report we could not settle.
        if (!cancelled) setGateState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [needsGate, patientId, gateCheckKey]);

  const completeAndOpen = async () => {
    if (!activeAssessment) return;
    setGateState('completing');
    try {
      const res = await finishAssessment(activeAssessment.id);
      if (res.data?.succeeded === false) {
        showSnackbar(res.data?.messages?.[0] || 'Could not complete the assessment.', 'error');
        setGateState('confirm');
        return;
      }
      setActiveAssessment(null);
      setGateState('open');
      showSnackbar('Assessment completed.', 'success');
    } catch (e) {
      showSnackbar(e?.response?.data?.messages?.[0] || 'Could not complete the assessment.', 'error');
      setGateState('confirm');
    }
  };

  useEffect(() => {
    if (data.reportDataArray || !patientId) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await getAllPatientReports(patientId);
        if (!cancelled) setFetchedReports(res.data?.data || []);
      } catch (e) {
        if (!cancelled) setFetchedReports([]);
      }
    })();

    return () => { cancelled = true; };
  }, [patientId, data.reportDataArray]);

  // Memoized merged report data
  const mergedReportData = useMemo(() => {
    if (!reportDataArray?.length) {
      return { domains: [], createdAt: new Date(), patientName, staffName };
    }

    const sortedReports = [...reportDataArray].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    const merged = {
      domains: [],
      createdAt: sortedReports[0].createdAt,
      patientName,
      staffName: sortedReports[0]?.staffName ?? staffName,
    };

    const filledDomains = new Set();

    for (const report of sortedReports) {
      for (const domainItem of report.domains || []) {
        const code = domainItem.domain?.code;
        if (!code) continue;

        if (!filledDomains.has(code) && domainItem.answers?.length) {
          merged.domains.push({ ...domainItem });
          filledDomains.add(code);
        }
      }

      if (filledDomains.size === domains.length) break;
    }

    return merged;
  }, [reportDataArray, patientName, staffName, domains.length]);

  // Fetch categories
  useEffect(() => {
    getCategory()
      .then((res) => {
        const formatted = (res.data?.data || []).map((item) => ({
          code: item.code,
          label: item.name
        }));
        setDomains(formatted);
      })
      .catch((err) => console.error('Failed to fetch categories:', err));
  }, []);

  // Fetch patient details
  useEffect(() => {
    const fetchPatient = async () => {
      try {
        let patient = null;

        if (!patient) {
          const res = await getPatientById(patientId);
          if (res.data?.succeeded && res.data.data) {
            const p = res.data.data;
            patient = {
              id: p.id,
              name: p.name,
              mobileNo: p.mobileNo,
              dateOfBirth: p.dateOfBirth,
              gender: p.gender,
              city: p.city || '-',
              state: p.state || '-',
              email: p.email,
              address: p.address,
              source: 'local',
            };
          }
        }

        setPatientDetails(patient);
      } catch (err) {
        console.error('Error fetching patient:', err);
      }
    };

    fetchPatient();
  }, [patientId]);

  // Fetch therapy recommendations
  useEffect(() => {
    const fetchTherapyRecommendations = async () => {
      const recommendations = [];
      const concernItems = []; 
      for (const domainItem of mergedReportData.domains || []) {
        const { code } = domainItem.domain;

        const allQuestionsRes = await getQuestionsByDomain(code);
        const allQuestions = allQuestionsRes?.data?.data || [];

        if (code === "SEN" || code === "BEH") {
          const questionsWithAnswers = allQuestions.map((question) => {
            const answer = domainItem.answers.find((a) => a.questionId === question.id);
            return {
              ...question,
              staffResponse: answer?.staffResponse ?? null,
              parentResponse: answer?.parentResponse ?? null,
            };
          });

          questionsWithAnswers.forEach((q) => {
            if (q.staffResponse === 2 && q.parentResponse === 2) {
              concernItems.push({
                question: q.question,
                description: q.description,
                domainLabel: domainItem.domain?.name,
              });
            }
          });
        } else {
          const questionsWithAnswers = allQuestions
            .map((question) => {
              const answer = domainItem.answers.find((a) => a.questionId === question.id);
              return {
                ...question,
                staffResponse: answer?.staffResponse ?? null,
                parentResponse: answer?.parentResponse ?? null,
              };
            })
            .sort((a, b) => a.position - b.position);

          let lastAttemptedIndex = -1;
          questionsWithAnswers.forEach((q, idx) => {
            if (q.staffResponse !== null || q.parentResponse !== null) {
              lastAttemptedIndex = idx;
            }
          });

          if (lastAttemptedIndex !== -1) {
            for (let i = 0; i <= lastAttemptedIndex; i++) {
              const q = questionsWithAnswers[i];
              const isAchieved = q.staffResponse === 2 && q.parentResponse === 2;
              if (!isAchieved) {
                recommendations.push({
                  question: q.question,
                  description: q.description,
                  domainLabel: domainItem.domain?.name,
                });
              }
            }

            const afterLast = questionsWithAnswers.slice(lastAttemptedIndex + 1);
            const firstUnattempted = afterLast.find(
              (q) => q.staffResponse === null && q.parentResponse === null
            );
            if (firstUnattempted) {
              recommendations.push({
                question: firstUnattempted.question,
                description: firstUnattempted.description,
                domainLabel: domainItem.domain?.name,
              });
            }
          } else {
            const firstQuestion = questionsWithAnswers[0];
            if (firstQuestion) {
              recommendations.push({
                question: firstQuestion.question,
                description: firstQuestion.description,
                domainLabel: domainItem.domain?.name,
              });
            }
          }
        }
      }
      setConcerns(concernItems);
      setTherapyDescriptions(recommendations);
    };

    if (mergedReportData?.domains?.length) {
      fetchTherapyRecommendations();
    }
  }, [mergedReportData]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await getPatientHistory(patientId);
        if (res.data.succeeded) {
          const allHistory = res.data.data;
          const cutoffDateOnly = dayjs(mergedReportData.createdAt).format("YYYY-MM-DD");
          const filtered = allHistory.filter((h) => {
            const hDate = dayjs(h.submittedAt).format("YYYY-MM-DD");
            return hDate <= cutoffDateOnly;
          });

          setHistory(filtered);
        }
      } catch (err) {
        console.error('Error fetching history:', err);
      } finally {
        setLoading(false);
      }
    };

    if (mergedReportData?.createdAt) {
      fetchHistory();
    }
  }, [patientId, mergedReportData]);

  const { download: handleDownload, exporting } = useReportExport({
    elementRef: reportRef,
    filename: `${(patientName || patientDetails?.name || mergedReportData?.patientName || 'patient').replace(/\s+/g, '_')}_Report`,
  });

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Filtered therapy descriptions based on timeframe
  const filteredTherapyDescriptions = useMemo(() => {
    if (!therapyDescriptions.length) return [];

    const perDomainLimit = timeframe === "1month" ? 2 : 5;

    const grouped = therapyDescriptions.reduce((acc, item) => {
      const domain = item.domainLabel || "Unknown";
      if (!acc[domain]) acc[domain] = [];
      acc[domain].push(item);
      return acc;
    }, {});

    const limited = Object.values(grouped).flatMap((items) =>
      items.slice(0, perDomainLimit)
    );

    return limited;
  }, [therapyDescriptions, timeframe]);

  useEffect(() => {
    if (filteredTherapyDescriptions.length > 0) {
      const initialState = filteredTherapyDescriptions.reduce((acc, _, idx) => {
        acc[idx] = { question: true, description: false };
        return acc;
      }, {});
      setCheckedDescriptions(initialState);
    } else {
      setCheckedDescriptions({});
    }
  }, [filteredTherapyDescriptions]);

  const scoredDomainCount = activeAssessment
    ? (() => {
        const keys = scoredDomainKeys(gateHistory, activeAssessment);
        return domains.filter((c) => isDomainScored(keys, c)).length;
      })()
    : 0;

  if (!patientId) return <Navigate to="/patient" replace />;

  if (gateState !== 'open') {
    const totalDomains = domains.length || 10;
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Paper variant="outlined" sx={{ p: 4 }}>
          {gateState === 'checking' && (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={22} />
              <Typography>Checking assessment status…</Typography>
            </Stack>
          )}

          {gateState === 'error' && (
            <Stack spacing={2}>
              <Alert severity="error">Could not check this patient's assessment status. Please try again.</Alert>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={() => navigate(`/patient/${patientId}/assess`)}>Back to domains</Button>
                <Button variant="contained" onClick={() => setGateCheckKey((k) => k + 1)}>Retry</Button>
              </Stack>
            </Stack>
          )}

          {(gateState === 'confirm' || gateState === 'completing') && (
            <Stack spacing={2}>
              <Typography variant="h6">End this assessment?</Typography>
              <Typography variant="body2" color="text.secondary">
                {scoredDomainCount} of {totalDomains} domains scored
                {patientDetails?.name ? ` for ${patientDetails.name}` : ''}.
              </Typography>
              <Alert severity={scoredDomainCount < totalDomains ? 'warning' : 'info'}>
                Viewing the progress report ends this paid assessment. You won't be able to score
                more domains, and the next assessment will need a new payment.
              </Alert>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  onClick={() => navigate(`/patient/${patientId}/assess`)}
                  disabled={gateState === 'completing'}
                >
                  Continue scoring
                </Button>
                <Button
                  variant="contained"
                  onClick={completeAndOpen}
                  disabled={gateState === 'completing'}
                >
                  {gateState === 'completing' ? 'Ending…' : 'End assessment & view report'}
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
       <GlobalStyles styles={{
        '@media print': {
          '.exclude-pdf': { display: 'none !important' },
          '.apexcharts-menu-icon': { display: 'none !important' },
          '.apexcharts-toolbar': { display: 'none !important' },
          '.therapy-goal-item': {
            pageBreakInside: 'avoid',
            breakInside: 'avoid'
          },
          '.concerns-section, .therapy-goals-section, .doctor-remarks-section': {
            pageBreakInside: 'avoid !important',
            breakInside: 'avoid !important',
          },
        }
      }} />

      <Box textAlign="right" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="contained" onClick={() => navigate(`/patient/${patientId}/history`, { replace: true })}>
            History
          </Button>
          <Button variant="contained" onClick={handleDownload} disabled={exporting}>{exporting ? 'Preparing PDF…' : 'Download PDF'}</Button>
        </Stack>
      </Box>


      <div ref={reportRef}>
        <Box sx={{ textAlign: 'center', mb: 2 }} className='exclude-pdf'>
          <img src={logo} alt="Vqure" style={{ height: 40, marginRight: 10 }} />
        </Box>
        <Typography variant="h6" gutterBottom>Progress Report</Typography>

        <Box sx={{ mb: 2, border: '1px solid #ccc', p: 2 }}>
          <Typography variant="body1"><strong>Name:</strong> {patientName || patientDetails?.name || ''}</Typography>
          <Typography variant="body1">
            <strong>Age:</strong> {patientDetails?.dateOfBirth ? calculateAge(patientDetails.dateOfBirth) + " years" : ''}
          </Typography>
          <Typography variant="body1"><strong>Date of Assessment:</strong> {new Date(mergedReportData.createdAt).toLocaleDateString()}</Typography>
          <Typography variant="body1"><strong>Assessor's Name:</strong> {mergedReportData.staffName || ''}</Typography>
        </Box>

        {/* Score summary (domain / skills / percentage): shown on screen only,
            excluded from the downloaded PDF per clinic request. */}
        <Table className="exclude-pdf">
          <TableHead>
            <TableRow>
              <TableCell><strong>Domain</strong></TableCell>
              <TableCell>Skills Achieved</TableCell>
              <TableCell>Percentage Achieved</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {domains
              .filter((domainItem) => domainItem.code !== 'BEH' && domainItem.code !== 'SEN')
              .map((domainItem) => {
              const domainReport = mergedReportData.domains.find(
                (d) => d.domain.code === domainItem.code
              );

              let skillsAchieved = 0;

              if (domainReport && Array.isArray(domainReport.answers)) {
                domainReport.answers.forEach((answer) => {
                  if (answer.staffResponse === 2 && answer.parentResponse === 2) {
                    skillsAchieved++;
                  }
                });
              }
              const progress = getDomainProgress(domainItem.code, skillsAchieved);

              return (
                <TableRow key={domainItem.code}>
                  <TableCell>{domainItem.label}</TableCell>
                  <TableCell>{skillsAchieved}</TableCell>
                  <TableCell>
                    {progress ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ width: 50 }}>{`${progress.percentage}%`}</Typography>
                        <Tooltip title={`Level ${progress.level}`}>
                          <Chip
                            label={`L ${progress.level}`}
                            size="small"
                            sx={{
                              fontWeight: 'bold',
                              backgroundColor: levelColors[progress.level] || '#999',
                              color: '#fff',
                              height: 24,
                            }}
                          />
                        </Tooltip>
                      </Stack>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {/* Concerns Section */}
        <Box 
          className="concerns-section" 
          sx={{ 
            mt: 4, 
            mb: 5,
            pageBreakInside: 'avoid',
            breakInside: 'avoid'
          }}
        >
          <Typography variant="h6">Concerns:</Typography>
          <Box sx={{ border: '1px solid #ccc', minHeight: 80, mt: 1, p: 2 }}>
            {concerns.length > 0 ? (
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {concerns.map((item, idx) => (
                  <li key={idx} style={{ marginBottom: '12px' }}>
                    <Typography variant="body2">
                      <strong>{item.domainLabel}:</strong> {item.question}
                    </Typography>
                  </li>
                ))}
              </ul>
            ) : (
              <Typography variant="body2" color="text.secondary">-</Typography>
            )}
          </Box>
        </Box>

        {/* Therapy Goals Section */}
        <Box 
          className="therapy-goals-section" 
          sx={{ 
            mt: 4, 
            mb: 5,
            pageBreakInside: 'avoid',
            breakInside: 'avoid'
          }}
        >
           <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Therapy Goals:</Typography>
            <RadioGroup
              row
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              className="exclude-pdf"
            >
              <FormControlLabel value="1month" control={<Radio />} label="1 Month" />
              <FormControlLabel value="3month" control={<Radio />} label="3 Months" />
            </RadioGroup>
          </Stack>

          <Box sx={{ border: '1px solid #ccc', minHeight: 80, mt: 1, p: 2 }}>
            {filteredTherapyDescriptions.length > 0 ? (
              <div className="therapy-goals">
                {filteredTherapyDescriptions.map((desc, idx) => (
                  <div key={idx} style={{ marginBottom: '16px' }}>
                    {/* Question checkbox */}
                    <div
                      className={`therapy-goal-item ${checkedDescriptions[idx]?.question ? "is-selected" : "is-unselected"}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                        pageBreakInside: "avoid",
                        breakInside: "avoid",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkedDescriptions[idx]?.question || false}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setCheckedDescriptions((prev) => ({
                                ...prev,
                                [idx]: {
                                  question: isChecked,
                                  description: isChecked ? true : false,
                                },
                              }));
                            }}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.secondary">
                            <strong>{desc.domainLabel}:</strong> {desc.question}
                          </Typography>
                        }
                        sx={{ alignItems: "center", margin: 0 }}
                      />
                    </div>

                    {/* Description checkbox */}
                    {desc.description && (
                      <div
                        className={`therapy-goal-item ${checkedDescriptions[idx]?.description ? "is-selected" : "is-unselected"}`}
                        style={{
                          marginBottom: "12px",
                          pageBreakInside: "avoid",
                          breakInside: "avoid",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checkedDescriptions[idx]?.description || false}
                              onChange={(e) =>
                                setCheckedDescriptions((prev) => ({
                                  ...prev,
                                  [idx]: {
                                    ...prev[idx],
                                    description: e.target.checked,
                                  },
                                }))
                              }
                              color="primary"
                              disabled={!checkedDescriptions[idx]?.question}
                            />
                          }
                          label={
                            <Typography variant="body2" color="text.secondary">
                              {desc.description}
                            </Typography>
                          }
                          sx={{ alignItems: "center", margin: 0 }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </Box>
        </Box>

        {/* Doctor Remarks */}
        <Box
          className="doctor-remarks-section"
          sx={{
            mt: 2,
            mb: 5,
            pageBreakInside: 'avoid',
            breakInside: 'avoid',
          }}
        >
          <Typography variant="h6">Doctor remarks:</Typography>
          <Box
            className="doctor-remarks-input"
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Doctor remarks"
            data-placeholder="Type remarks here, or leave blank to write by hand on the printout"
            sx={{
              pageBreakInside: 'avoid',
              breakInside: 'avoid',
            }}
          />
        </Box>

        {/* Chart */}
        <Box
          className="chart-wrapper exclude-pdf"
          sx={{
            mt: 2,
            mb: 5,
            width: '100%',
            maxWidth: '700px',
            mx: 'auto',
            pageBreakBefore: 'auto',
            breakBefore: 'auto'
          }}
        >
          {loading && history.length === 0 ? (
            <Typography>No assessments found on selected date.</Typography>
          ) : (
            <BarChartLevel data={history} />
          )}
        </Box>
            
      </div>
    </Container>
  );
};

export default ReportDetail;